import { KeyValuePipe, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  linkedSignal,
  OnChanges,
  OnInit,
  output,
  signal,
  SimpleChanges,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  FormArray,
  FormControl,
  FormGroup,
  FormGroupDirective,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import {
  AddressLineControls,
  AddressLineGroup,
  AddressNew,
  AddressMode,
} from '../../types';
import {
  WcgInputFieldComponent,
  WcgMenuOption,
  WcgUniSelectComponent,
} from '@wcg/frontend-framework';

import { DIALOG_MODES } from '../../constants';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    WcgInputFieldComponent,
    WcgUniSelectComponent,
    KeyValuePipe,
    TitleCasePipe,
  ],
  selector: 'wcg-address-form',
  styleUrls: ['./address-form.component.css'],
  templateUrl: './address-form.component.html',
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class AddressFormComponent implements OnChanges, OnInit {
  private readonly _destroyRef = inject(DestroyRef);
  public readonly controlContainer = inject(ControlContainer, {
    optional: true,
  });

  readonly addressLineLabels = input<string[]>([]);
  readonly addressMode = input<AddressMode>(DIALOG_MODES.CREATE);
  readonly currentAddress = input<AddressNew>({} as AddressNew);
  readonly countries = input<WcgMenuOption[]>([]);
  readonly states = input<WcgMenuOption[]>([]);
  countryId = output<string>();

  readonly $addressLines: WritableSignal<FormArray<AddressLineGroup>> =
    linkedSignal({
      computation: (labels, previous) => {
        if (!previous) {
          return this._updateAddressLines(labels);
        }
        return previous.value;
      },
      source: this.addressLineLabels,
    });

  readonly $stateGroup = linkedSignal({
    computation: (states) => {
      const _country = this.controlContainer?.control?.get('countryId')
        ?.value as WcgMenuOption;
      if (
        (_country?.label?.length > 0 &&
          _country?.label?.toLowerCase() === 'united states') ||
        _country?.label?.toLowerCase() === 'canada'
      ) {
        return states;
      }
      return [];
    },
    source: this.states,
  });

  readonly stateLabel = signal<string>('State');

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['states']) {
      this._patchState();
    }
  }

  ngOnInit() {
    this._customListeners();
  }

  private _updateAddressLines(formLabels: string[]) {
    const _canEdit = this.addressMode() === DIALOG_MODES.EDIT;
    const _addressLines = this.controlContainer?.control?.get(
      'addressLines',
    ) as FormArray<AddressLineGroup>; //TWO-WAY BINDING;
    if (!_addressLines) {
      return _addressLines;
    }
    // Only push a new group when the array is empty. When the parent (e.g. address
    // wrapper) has already pushed a group (e.g. from autocomplete selection), we must
    // not push again or address line 1, 2 and city will appear duplicated.
    if (_addressLines.length > 0) {
      return _addressLines;
    }
    const _addressLineControls = {} as AddressLineControls;
    for (const label of formLabels) {
      /**
       * labels can contain white spaces. The below line will remove them,
       * And also we use the same labels for display purpose and which
       * are different from AddressLineGroup. we are replacing the matching
       * labels with the right one.
       */
      const _key = label.replace(/\s/g, '').toLowerCase();
      const _k = _key.includes('addressline')
        ? _key.replace('addressline', 'address')
        : _key;

      /**
       * The following will determine the patching value.
       * If it's a creation mode will get an empty string
       * and if it's an edit mode will patch value from currentAddress
       */
      const fValue = (
        _canEdit && _k in this.currentAddress()
          ? this.currentAddress()[_k as keyof AddressNew]
          : ''
      ) as string;

      /**
       * Validators are only set if it's either an 'address1' || 'city'
       */
      Object.assign(_addressLineControls, {
        [_k]: new FormControl<string>(fValue, {
          nonNullable: true,
          validators: [
            ...(_k === 'address1' || _k === 'city'
              ? [Validators.required]
              : []),
          ],
        }),
      });
    }
    _addressLines.push(new FormGroup(_addressLineControls));
    return _addressLines;
  }

  /**
   * The following method is required. Since state changes are dependent on the countryId change.
   * In the above method at line 119 patch countryId will trigger the subscription for countryID valueChanges.
   * Which will then fetch states based on the country. Once the states are available. We will patch state formControl explicitly along with zipcode.
   *
   */
  private _patchState(): void {
    if (
      this.addressMode() === DIALOG_MODES.EDIT &&
      Object.values(this.currentAddress()!).length > 0
    ) {
      const address = this.currentAddress();
      const countryName = address.country.name.toLowerCase();
      const _isUnitedStatesOrCanada =
        countryName === 'united states' || countryName === 'canada';
      if (this.controlContainer?.control?.disabled) {
        this.$addressLines().disable({ emitEvent: false });
      }
      if (
        this.controlContainer?.control
          ?.get('countryId')
          ?.value?.label?.toLowerCase() === countryName.toLowerCase()
      ) {
        const _state = _isUnitedStatesOrCanada
          ? this.states().find(
              (v) =>
                v.label?.toLocaleLowerCase() === address.state.toLowerCase(),
            )
          : address.state;
        this.controlContainer?.control?.patchValue(
          {
            state: _state,
            zipCode: _state ? address.zipCode : '',
          },
          { emitEvent: false },
        );
        console.log(`patched state: ${this.controlContainer?.value}`);
      }
    }
  }

  private _updateStateLabel(country: WcgMenuOption) {
    this.stateLabel.update(() => {
      switch (country?.label?.toLowerCase()) {
        case 'united states':
          return 'State';
        case 'canada':
          return 'Province';
        default:
          return 'State/Province';
      }
    });
  }

  private _customListeners() {
    this.controlContainer?.control
      ?.get('countryId')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (country: WcgMenuOption | null) => {
          if (country) {
            this._updateStateLabel(country);
            this.countryId.emit(country.id! ?? '');
            this.controlContainer?.control?.patchValue({
              state: '',
              zipCode: '',
            });
            this.$stateGroup.set([]);
          }
        },
      });
  }
}
